/**
 * Bong 精选作品预览数据（点击展示卡 → 预览页）
 *
 * - 模板卡（设计稿构图卡）：id 为 wf-01..wf-13（图像）/ v-01..v-20（视频），
 *   文案与 WorksSection 的 TEMPLATE_* 常量保持一致（单一事实来源见 WorksSection 注释）；
 *   视频模板卡播放源为 public/videos/v-XX.mp4（由封面 PNG 经 ffmpeg Ken Burns 生成的演示视频，
 *   与卡片封面一一对应），保证预览页「自动无声播放 + 进度条拖拽」有真实媒体可用。
 * - 用户作品（pub-<uuid>）：从 worksStore.publishedWorks 反查补全预览字段。
 * - resolveFeaturedItem：卡片视图模型 → 预览页数据模型的统一入口（AppNew 点击链路调用）。
 */
import type { PublishedWork, WorkflowSnapshot } from '../stores/worksStore';

/** 预览页作品数据模型（图像 188:19 / 视频 202:501 两页共用） */
export interface FeaturedPreviewItem {
  id: string;
  type: 'image' | 'video';
  /** 图片作品 = 封面首图；视频作品 = 视频地址 */
  url: string;
  /** 视频封面（缩略图条用） */
  coverUrl?: string;
  /** 视频多档清晰度源（用户发布作品经后端异步转码生成；模板视频为静态单源无此字段） */
  sources?: VideoSource[];
  /** 图片作品图集（含首图），驱动中央大图 / 左侧缩略图列 / 右栏图集三处联动 */
  images?: string[];
  title: string;
  author: string;
  avatar?: string;
  likes: string;
  /** 展示日期 YYYY-MM-DD */
  date: string;
  /** 图片作品：图片提示词 */
  prompt?: string;
  /** 视频作品：作品描述 */
  description?: string;
  /** 生成模型（Bong Image 2.0 / Seedance 2.5） */
  model: string;
  /** 画面比例（9:16 / 16:9） */
  aspect: string;
  /** 画质档位（2K） */
  quality: string;
  /** 是否展示「智能参考」参数 */
  hasSmartRef?: boolean;
  /** 是否公开制作流程（「查看制作流程」按钮还需 workflowData 非空才显示） */
  isPublicWorkflow?: boolean;
  /** 公开制作流程快照（发布时源项目全部节点/连线/分组；查看制作流程画布与复制项目的数据源，
      与实际节点一致。模板卡/旧数据无此字段 → 不显示「查看制作流程」入口） */
  workflowData?: WorkflowSnapshot;
  /** 视频预览页底部缩略图条（相关视频，当前作品之外的条目） */
  related?: FeaturedRelatedVideo[];
}

/** 多档清晰度源（后端异步转码生成，按清晰度从高到低；未转码完成时缺省 → 播放器懒查询兜底） */
export interface VideoSource {
  label: string;
  url: string;
}

export interface FeaturedRelatedVideo {
  id: string;
  title: string;
  cover: string;
  videoUrl?: string;
  sources?: VideoSource[];
}

/* ── 模板文案（与 WorksSection TEMPLATE_* 一致） ── */
const TEMPLATE_TITLE = 'IPhone Air 极简纯净风产品展示';
const VIDEO_TEMPLATE_TITLE = 'iPhone air 极简纯净风产品展示';
const TEMPLATE_USER = '用户7500';
const TEMPLATE_LIKES = '1684';
const TEMPLATE_DATE = '2026-09-24';

const TEMPLATE_PROMPT =
  '极简纯净风格产品展示，纯白背景，柔和影棚灯光，突出 iPhone Air 轻薄机身与钛金属质感，' +
  '产品居中特写，画面干净无杂物，9:16 竖版构图，2K 画质，商业摄影级细节';

const TEMPLATE_DESCRIPTION =
  '使用 Seedance 生成的 iPhone Air 极简纯净风产品展示视频：纯净背景搭配柔和光影推移，' +
  '镜头缓慢推近呈现机身细节，整体节奏舒缓，适合电商主图视频与社媒投放素材。';

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * 图片作品图集：主图 + 同作品 4 张衍生视图（gallery/wf-XX-02..05，由封面预生成——
 * 取景特写 / 镜像机位 / 下部构图 / 居中大特写，并做色调微调），全部取自本作品自身素材，
 * 与设计稿图集行一致（设计稿 5 格即同一作品的不同取景变体），不使用其他作品封面。
 */
function buildTemplateGallery(key: string): string[] {
  return [
    `/images/home/waterfall/${key}.png`,
    `/images/home/waterfall/gallery/${key}-02.png`,
    `/images/home/waterfall/gallery/${key}-03.png`,
    `/images/home/waterfall/gallery/${key}-04.png`,
    `/images/home/waterfall/gallery/${key}-05.png`,
  ];
}

/** 模板图像作品（188:19 预览页数据） */
export function buildTemplateImageItem(key: string): FeaturedPreviewItem {
  return {
    id: key,
    type: 'image',
    url: `/images/home/waterfall/${key}.png`,
    images: buildTemplateGallery(key),
    title: TEMPLATE_TITLE,
    author: TEMPLATE_USER,
    likes: TEMPLATE_LIKES,
    date: TEMPLATE_DATE,
    prompt: TEMPLATE_PROMPT,
    model: 'Bong Image 2.0',
    aspect: '9:16',
    quality: '2K',
    hasSmartRef: true,
  };
}

/** 模板视频作品（202:501 预览页数据；播放源为生成的演示视频） */
export function buildTemplateVideoItem(key: string): FeaturedPreviewItem {
  const index = parseInt(key.slice(2), 10); // v-07 → 7
  /* 相关视频 = 除当前外的全部模板视频（v-01..v-20 全量可达，底部切换条可切换
     所有模板作品；offset 0..18 环绕一周恰好跳过自身） */
  const related: FeaturedRelatedVideo[] = Array.from({ length: 19 }, (_, i) => {
    const n = ((index + i) % 20) + 1;
    const id = `v-${pad2(n)}`;
    return { id, title: VIDEO_TEMPLATE_TITLE, cover: `/images/home/videos/${id}.png`, videoUrl: `/videos/${id}.mp4` };
  });
  return {
    id: key,
    type: 'video',
    url: `/videos/${key}.mp4`,
    coverUrl: `/images/home/videos/${key}.png`,
    title: VIDEO_TEMPLATE_TITLE,
    author: TEMPLATE_USER,
    likes: TEMPLATE_LIKES,
    date: TEMPLATE_DATE,
    description: TEMPLATE_DESCRIPTION,
    model: 'Seedance 2.5',
    aspect: '16:9',
    quality: '2K',
    hasSmartRef: true,
    isPublicWorkflow: true,
    related,
  };
}

const padFromDate = (ts: number) => {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** 用户发布作品 → 预览数据（workflowData/isPublicWorkflow 原样透传，查看制作流程数据源） */
function buildUserItem(work: PublishedWork): FeaturedPreviewItem {
  if (work.type === 'video') {
    return {
      id: work.id,
      type: 'video',
      url: work.url,
      coverUrl: work.coverUrl || '',
      sources: work.sources,
      title: work.title,
      author: work.authorName,
      avatar: work.authorAvatar,
      likes: String(work.likes),
      date: padFromDate(work.createdAt),
      description: work.prompt,
      model: 'Seedance 2.5',
      aspect: '16:9',
      quality: '2K',
      hasSmartRef: true,
      isPublicWorkflow: work.isPublicWorkflow,
      workflowData: work.workflowData,
    };
  }
  const images = work.images && work.images.length ? work.images : [work.coverUrl || work.url];
  return {
    id: work.id,
    type: 'image',
    url: images[0],
    images,
    title: work.title,
    author: work.authorName,
    avatar: work.authorAvatar,
    likes: String(work.likes),
    date: padFromDate(work.createdAt),
    prompt: work.prompt,
    model: 'Bong Image 2.0',
    aspect: '9:16',
    quality: '2K',
    hasSmartRef: true,
    isPublicWorkflow: work.isPublicWorkflow,
    workflowData: work.workflowData,
  };
}

/** 卡片视图模型（WorkCard）→ 预览页数据；用户作品查 worksStore，模板卡查本表 */
export function resolveFeaturedItem(
  card: { id?: string; key: string; videoUrl?: string; title?: string },
  works: PublishedWork[]
): FeaturedPreviewItem | null {
  // 用户作品：WorksSection 构造时 id = w.id、key = `pub-${w.id}`
  if (card.id && !card.id.startsWith('wf-') && !card.id.startsWith('v-')) {
    const work = works.find((w) => w.id === card.id);
    return work ? buildUserItem(work) : null;
  }
  const id = card.id || card.key;
  const isVideo = !!card.videoUrl || id.startsWith('v-');
  return isVideo ? buildTemplateVideoItem(id) : buildTemplateImageItem(id);
}

/** 刷新恢复：按 type + id 解析（URL /featured?type=image&id=wf-01） */
export function resolveFeaturedById(type: 'image' | 'video', id: string, works: PublishedWork[]): FeaturedPreviewItem | null {
  const work = works.find((w) => w.id === id);
  if (work && work.type === type) return buildUserItem(work);
  if (/^(wf-\d{2})$/.test(id) && type === 'image') return buildTemplateImageItem(id);
  if (/^(v-\d{2})$/.test(id) && type === 'video') return buildTemplateVideoItem(id);
  return null;
}
